import * as Linking from 'expo-linking';
import { Ionicons } from '@expo/vector-icons';
import { useFocusEffect, useRouter } from 'expo-router';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Animated,
  Modal,
  Platform,
  Pressable,
  StyleSheet,
  Text,
  View,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { CHAT_AQUA, fontSize, radius, spacing } from '@/constants/theme';
import type { AppColors } from '@/constants/theme';
import { useTheme } from '@/contexts/ThemeContext';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';
import { IconActionButton, type IconActionKey } from '@/components/ui/IconActionButton';

/** Πόσα εισερχόμενα μηνύματα (όχι δικά μας) έχουν έρθει μετά την τελευταία
 * φορά που ο χρήστης διάβασε αυτό το chat — ίδια λογική με το
 * get_action_breakdown().unread_chat, αλλά ανά συγκεκριμένη συμφωνία. */
async function fetchUnreadCount(agreementId: string, userId: string): Promise<number> {
  const { data: readRow } = await (supabase as any).from('agreement_message_reads')
    .select('last_read_at')
    .eq('agreement_id', agreementId)
    .eq('user_id', userId)
    .maybeSingle();
  const since = readRow?.last_read_at ?? '1970-01-01T00:00:00Z';
  const { count } = await supabase
    .from('agreement_messages')
    .select('id', { count: 'exact', head: true })
    .eq('agreement_id', agreementId)
    .neq('sender_id', userId)
    .gt('created_at', since);
  return count ?? 0;
}

/** Ίδια λογική με fetchUnreadCount, αλλά για in-app chat μεταξύ μελών κύκλου
 * (χωρίς συμφωνία) — βλ. connection_messages/connection_message_reads. */
async function fetchConnectionUnreadCount(connectionId: string, userId: string): Promise<number> {
  const { data: readRow } = await (supabase as any).from('connection_message_reads')
    .select('last_read_at')
    .eq('connection_id', connectionId)
    .eq('user_id', userId)
    .maybeSingle();
  const since = readRow?.last_read_at ?? '1970-01-01T00:00:00Z';
  const { count } = await (supabase as any)
    .from('connection_messages')
    .select('id', { count: 'exact', head: true })
    .eq('connection_id', connectionId)
    .neq('sender_id', userId)
    .gt('created_at', since);
  return count ?? 0;
}

type CommApp = 'viber' | 'whatsapp' | 'telegram' | 'signal' | 'call';

function getAppConfig(t: (key: string) => string): Record<CommApp, { label: string; icon: keyof typeof Ionicons.glyphMap; color: string; deepLink: (phone: string) => string }> {
  return {
    viber:    { label: 'Viber',           icon: 'call-outline',          color: '#7360F2', deepLink: (p) => `viber://chat?number=${p}` },
    whatsapp: { label: 'WhatsApp',        icon: 'logo-whatsapp',         color: '#25D366', deepLink: (p) => `whatsapp://send?phone=${p}` },
    telegram: { label: 'Telegram',        icon: 'paper-plane-outline',   color: '#2AABEE', deepLink: (p) => `tg://resolve?phone=${p}` },
    signal:   { label: 'Signal',          icon: 'lock-closed-outline',   color: '#3A76F0', deepLink: (p) => `sgnl://send?phone=${p}` },
    call:     { label: t('chatBubble.callLabel'), icon: 'phone-portrait-outline', color: '#34C759', deepLink: (p) => `tel:${p}` },
  };
}

/** Ποιες εφαρμογές επικοινωνίας είναι διαθέσιμες βάσει προφίλ (ίδια λογική παντού). */
export function getAvailableApps(profile: { phone?: string | null; communication_apps?: unknown } | null | undefined): CommApp[] {
  if (!profile) return [];
  const rawApps = (profile.communication_apps ?? []) as string[];
  const validApps = rawApps.filter((a): a is CommApp => ['viber', 'whatsapp', 'telegram', 'signal', 'call'].includes(a));
  // "call" works without phone; others need phone
  return validApps.filter((a) => a === 'call' || !!profile.phone);
}

type Target = {
  userId: string;
  label: string;
  phone: string | null;
  communication_apps?: unknown;
};

type Props = {
  otherUserId?: string | null | undefined;
  /** Ετικέτα δίπλα στο bubble (μονό-άτομο mode). */
  label?: string;
  /** Αν δοθεί, παρακάμπτεται το network fetch — χρήσιμο όταν το προφίλ είναι ήδη φορτωμένο. */
  preloadedProfile?: { phone: string | null; communication_apps?: unknown } | null;
  /** Multi-άτομο mode (συμφωνίες πολλών ατόμων) — ΕΝΑ bubble, ο χρήστης διαλέγει
   * πρώτα άτομο και μετά εφαρμογή, αντί να στοιβάζονται πολλά ξεχωριστά bubbles. */
  targets?: Target[];
  /** Ύψος τυχόν σταθερού footer με κουμπιά δράσης (μετρημένο με onLayout από
   * την οθόνη) — το bubble τοποθετείται πάντα πάνω από αυτό, ώστε να μην
   * πέφτει ποτέ πάνω σε action buttons ανεξάρτητα από το πόσο ψηλό γίνεται. */
  footerHeight?: number;
  /** Αν δοθεί, το bubble ρωτάει πρώτα "εντός Χειραψίας ή εκτός" — το εντός
   * ανοίγει το in-app chat της συγκεκριμένης συμφωνίας (πάντα διαθέσιμο,
   * ανεξάρτητα από το ποιους εξωτερικούς τρόπους έχει επιλέξει ο χρήστης). */
  agreementId?: string;
  /** Αν δοθεί (κύκλος-προφίλ, χωρίς κοινή συμφωνία), το in-app chat ανοίγει
   * τη συνομιλία μελών κύκλου (connection_messages) αντί για συμφωνίας. Αν
   * δοθούν ΚΑΙ τα δύο, προτεραιότητα έχει το agreementId. */
  connectionId?: string;
  /** Αν true, αποδίδεται σαν ένα κανονικό inline κουμπί μέσα στη ροή της
   * οθόνης (ίδιο ύψος/στυλ με τα υπόλοιπα action buttons) αντί για
   * επιπλέον floating κύκλο — χρήσιμο σε οθόνες όπου το floating bubble
   * καλύπτει άλλα κουμπιά δράσης. Χρησιμοποιεί την ίδια λογική/modal. */
  inline?: boolean;
  /** Όταν δίνονται, το inline κουμπί ζωγραφίζεται με την ίδια λογική
   * expand/lift-modal με τα υπόλοιπα IconActionButton (στρογγυλό, μοιράζεται
   * το ίδιο "μόνο ένα ανοιχτό τη φορά" state) αντί για το παλιό pill. */
  expandedKey?: IconActionKey | null;
  onRequestExpand?: (key: IconActionKey | null) => void;
};

export function ChatBubble({ otherUserId, label, preloadedProfile, targets, footerHeight = 0, agreementId, connectionId, inline = false, expandedKey, onRequestExpand }: Props) {
  const { user } = useAuth();
  const { colors } = useTheme();
  const { t } = useTranslation();
  const router = useRouter();
  const APP_CONFIG = useMemo(() => getAppConfig(t), [t]);
  const styles = useMemo(() => makeStyles(colors), [colors]);
  const insets = useSafeAreaInsets();
  const isMulti = targets !== undefined;
  const [apps, setApps] = useState<CommApp[]>([]);
  const [phone, setPhone] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const [selectedTarget, setSelectedTarget] = useState<Target | null>(null);
  // 'root' = επιλογή εντός/εκτός (μόνο όταν υπάρχει agreementId) · 'apps' = η παλιά συμπεριφορά.
  const [step, setStep] = useState<'root' | 'apps'>('apps');
  const scaleAnim = useRef(new Animated.Value(0)).current;
  // Απαλός, συνεχής παλμός στο εικονίδιο του inline κουμπιού — τραβάει το
  // μάτι χωρίς να χρειάζεται floating bubble πάνω από άλλα κουμπιά.
  const pulseAnim = useRef(new Animated.Value(1)).current;
  useEffect(() => {
    const loop = Animated.loop(
      Animated.sequence([
        Animated.timing(pulseAnim, { toValue: 1.18, duration: 700, useNativeDriver: Platform.OS !== 'web' }),
        Animated.timing(pulseAnim, { toValue: 1, duration: 700, useNativeDriver: Platform.OS !== 'web' }),
      ]),
    );
    loop.start();
    return () => loop.stop();
  }, [pulseAnim]);

  // Αριθμός αδιάβαστων ΕΙΣΕΡΧΟΜΕΝΩΝ μηνυμάτων για το badge — όχι πλήθος
  // ατόμων στο chat. Ξαναφορτώνει κάθε φορά που η οθόνη παίρνει focus (π.χ.
  // επιστροφή από την οθόνη μηνυμάτων αφού διαβάστηκαν).
  const [unreadCount, setUnreadCount] = useState(0);
  useFocusEffect(useCallback(() => {
    if (!user?.id) return;
    let cancelled = false;
    if (agreementId) {
      void fetchUnreadCount(agreementId, user.id).then((n) => { if (!cancelled) setUnreadCount(n); });
    } else if (connectionId) {
      void fetchConnectionUnreadCount(connectionId, user.id).then((n) => { if (!cancelled) setUnreadCount(n); });
    }
    return () => { cancelled = true; };
  }, [agreementId, connectionId, user?.id]));

  // Πλευρά της οθόνης όπου κάθεται το bubble — εναλλάσσεται με μεγάλο πάτημα στο βελάκι.
  const [side, setSide] = useState<'left' | 'right'>('right');

  function toggleSide() {
    setSide((s) => (s === 'right' ? 'left' : 'right'));
  }

  // Το bubble φαίνεται κανονικά στην αρχή και μετά από 1 δευτερόλεπτο τραβιέται
  // εκτός οθόνης ώστε να μην καταλαμβάνει χώρο — μένει ορατό μόνο το βελάκι στην
  // άκρη. Πάτημα στο βελάκι το "τραβάει" ξανά μέσα· πάτημα οπουδήποτε αλλού το
  // κρύβει ξανά.
  const [expanded, setExpanded] = useState(true);
  // Το βελάκι δείχνεται μόνο αφού το bubble έχει ΠΡΑΓΜΑΤΙΚΑ ολοκληρώσει το
  // slide-out — αλλιώς για ~260ms (όση διαρκεί το animation) θα φαινόταν
  // ταυτόχρονα με το ακόμα ορατό, μισο-κρυμμένο bubble, επικαλύπτοντάς το.
  const [fullyCollapsed, setFullyCollapsed] = useState(false);
  const openAnim = useRef(new Animated.Value(1)).current; // 1 = ορατό, 0 = κρυμμένο εκτός οθόνης
  const HIDE_OFFSET = 70;

  useEffect(() => {
    const timer = setTimeout(() => setExpanded(false), 1000);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (expanded) setFullyCollapsed(false);
    Animated.timing(openAnim, { toValue: expanded ? 1 : 0, duration: 260, useNativeDriver: Platform.OS !== 'web' }).start(({ finished }) => {
      if (finished && !expanded) setFullyCollapsed(true);
    });
  }, [expanded, openAnim]);

  const slideTranslateX = openAnim.interpolate({
    inputRange: [0, 1],
    outputRange: [side === 'right' ? HIDE_OFFSET : -HIDE_OFFSET, 0],
  });

  useEffect(() => {
    if (isMulti || !otherUserId) return;
    if (preloadedProfile !== undefined) {
      setPhone(preloadedProfile?.phone ?? null);
      setApps(getAvailableApps(preloadedProfile));
      return;
    }
    void supabase
      .rpc('get_profiles_public', { p_ids: [otherUserId] })
      .then(({ data }) => {
        const p = data?.[0];
        if (!p) return;
        setPhone(p.phone ?? null);
        setApps(getAvailableApps(p));
      });
  }, [isMulti, otherUserId, preloadedProfile]);

  // Το in-app chat είναι πάντα διαθέσιμο σε μια συμφωνία, ανεξάρτητα από το αν
  // ο άλλος έχει επιλέξει εξωτερικούς τρόπους επικοινωνίας — το bubble δεν
  // πρέπει να εξαφανίζεται σε αυτή την περίπτωση.
  const hasAnything = !!agreementId || !!connectionId || (isMulti ? (targets?.length ?? 0) > 0 : apps.length > 0);

  useEffect(() => {
    Animated.spring(scaleAnim, {
      toValue: hasAnything ? 1 : 0,
      useNativeDriver: Platform.OS !== 'web',
      bounciness: 10,
      speed: 14,
    }).start();
  }, [hasAnything, scaleAnim]);

  if (!hasAnything) return null;

  function targetApps(target: Target): CommApp[] {
    return getAvailableApps({ phone: target.phone, communication_apps: target.communication_apps });
  }

  async function openApp(app: CommApp, targetPhone: string | null) {
    const p = targetPhone ?? '';
    const url = APP_CONFIG[app].deepLink(p.replace(/\s+/g, ''));
    setOpen(false);
    setSelectedTarget(null);
    try {
      const supported = await Linking.canOpenURL(url);
      if (supported) {
        await Linking.openURL(url);
      } else {
        // "call" fallback — dial even if deep link not recognized
        if (app === 'call') await Linking.openURL(`tel:${p}`);
      }
    } catch { /* silent */ }
  }

  function onPressBubble() {
    if (isMulti && (targets?.length ?? 0) === 1) {
      setSelectedTarget(targets![0]);
    } else {
      setSelectedTarget(null);
    }
    setStep(agreementId || connectionId ? 'root' : 'apps');
    setOpen(true);
  }

  function onOpenInAppChat() {
    setOpen(false);
    setSelectedTarget(null);
    if (agreementId) {
      router.push({ pathname: '/(app)/agreements/messages/[id]', params: { id: agreementId } } as any);
    } else if (connectionId) {
      router.push({ pathname: '/(app)/circle/messages/[id]', params: { id: connectionId, otherId: otherUserId ?? '', name: label ?? '' } } as any);
    }
  }

  function onPressArrow() {
    setExpanded((e) => !e);
  }

  // Αρκετά ψηλά ώστε να μη σκεπάζει τα κουμπιά δράσης (π.χ. "Τερματισμός
  // συνεργασίας" / "Εκτύπωση PDF") που κάθονται πάνω από το tab bar σε
  // πολλές οθόνες συμφωνιών. Όταν η οθόνη περνάει το πραγματικό μετρημένο
  // ύψος του footer (footerHeight), το bubble κάθεται πάντα πάνω από αυτό —
  // ανεξάρτητα από το πόσα action buttons στοιβάζονται.
  const bottom = footerHeight > 0 ? footerHeight + spacing.md : insets.bottom + 170;
  const showingApps = !isMulti || !!selectedTarget;
  const activeApps = isMulti ? (selectedTarget ? targetApps(selectedTarget) : []) : apps;
  const activePhone = isMulti ? (selectedTarget?.phone ?? null) : phone;

  // Το κάτω άκρο του κύκλου (54px) μέσα στο bubble — η ετικέτα (αν υπάρχει)
  // κάθεται ΚΑΤΩ από τον κύκλο, οπότε το κάτω άκρο του κύκλου είναι πιο ψηλά
  // από το bottom του ίδιου του bubble container κατά το ύψος του label block.
  const labelBlockHeight = !!label && !isMulti ? 29 : 0;
  const circleBottom = bottom + labelBlockHeight;
  const peekTabBottom = circleBottom - (44 - 54) / 2;

  function renderSheet() {
    return (
      <Modal
        visible={open}
        transparent
        animationType="fade"
        onRequestClose={() => { setOpen(false); setSelectedTarget(null); }}
      >
        <Pressable style={styles.overlay} onPress={() => { setOpen(false); setSelectedTarget(null); }} accessibilityRole="button">
          <Pressable style={[styles.sheet, { paddingBottom: insets.bottom + spacing.md }]} onPress={() => {}}>
            <View style={styles.handle} />
            <View style={styles.sheetHeader}>
              {step === 'apps' && (agreementId || connectionId) ? (
                <Pressable style={styles.sheetBackRow} onPress={() => { setStep('root'); setSelectedTarget(null); }} accessibilityRole="button">
                  <Ionicons name="chevron-back" size={20} color={colors.text} />
                  <Text style={styles.sheetTitle} numberOfLines={1}>
                    {isMulti && selectedTarget ? selectedTarget.label : t('chatBubble.sheetTitle')}
                  </Text>
                </Pressable>
              ) : isMulti && selectedTarget ? (
                <Pressable style={styles.sheetBackRow} onPress={() => setSelectedTarget(null)} accessibilityRole="button">
                  <Ionicons name="chevron-back" size={20} color={colors.text} />
                  <Text style={styles.sheetTitle} numberOfLines={1}>{selectedTarget.label}</Text>
                </Pressable>
              ) : (
                <Text style={styles.sheetTitle}>
                  {step === 'root'
                    ? t('chatBubble.sheetTitle')
                    : isMulti
                      ? t('chatBubble.pickPersonTitle')
                      : (label ? t('chatBubble.sheetTitleFor', { name: label }) : t('chatBubble.sheetTitle'))}
                </Text>
              )}
              <Pressable onPress={() => { setOpen(false); setSelectedTarget(null); }} hitSlop={12} accessibilityRole="button" accessibilityLabel={t('chatBubble.closeAccessibilityLabel')}>
                <Ionicons name="close" size={22} color={colors.textMuted} />
              </Pressable>
            </View>

            {step === 'root' ? (
              <>
                <Pressable style={styles.appRow} onPress={onOpenInAppChat} accessibilityRole="button">
                  <View style={[styles.appIcon, { backgroundColor: CHAT_AQUA }]}>
                    <Ionicons name="chatbubbles-outline" size={20} color="#fff" />
                  </View>
                  <View style={{ flex: 1 }}>
                    <Text style={styles.appLabel}>{t('chatBubble.inAppOption')}</Text>
                    <Text style={styles.appSubLabel}>{t('chatBubble.recommendedBadge')}</Text>
                  </View>
                  <Ionicons name="chevron-forward" size={16} color={colors.textMuted} />
                </Pressable>
                <Pressable style={styles.appRow} onPress={() => setStep('apps')} accessibilityRole="button">
                  <View style={[styles.appIcon, { backgroundColor: colors.textMuted }]}>
                    <Ionicons name="call-outline" size={20} color="#fff" />
                  </View>
                  <Text style={styles.appLabel}>{t('chatBubble.externalOption')}</Text>
                  <Ionicons name="chevron-forward" size={16} color={colors.textMuted} />
                </Pressable>
              </>
            ) : (
              <>
                {/* Χωρίς agreementId (π.χ. bubble σε προφίλ, εκτός συγκεκριμένης
                    συμφωνίας) — το in-app chat δεν υπάρχει ακόμα, δείχνουμε το
                    πάντα-πρώτο, greyed-out με εξήγηση γιατί δεν είναι διαθέσιμο. */}
                {!agreementId && !connectionId && (
                  <View style={[styles.appRow, styles.appRowDisabled]}>
                    <View style={[styles.appIcon, { backgroundColor: colors.border }]}>
                      <Ionicons name="chatbubbles-outline" size={20} color={colors.textMuted} />
                    </View>
                    <View style={{ flex: 1 }}>
                      <Text style={[styles.appLabel, styles.appLabelDisabled]}>{t('chatBubble.inAppOption')}</Text>
                      <Text style={styles.appSubLabelDisabled}>{t('chatBubble.inAppUnavailableHint')}</Text>
                    </View>
                  </View>
                )}
                {isMulti && !selectedTarget ? (
                  targets!.map((target) => (
                    <Pressable key={target.userId} style={styles.appRow} onPress={() => setSelectedTarget(target)} accessibilityRole="button">
                      <View style={[styles.appIcon, { backgroundColor: colors.primary }]}>
                        <Text style={styles.appIconInitial}>{(target.label[0] ?? '?').toUpperCase()}</Text>
                      </View>
                      <Text style={styles.appLabel} numberOfLines={1}>{target.label}</Text>
                      <Ionicons name="chevron-forward" size={16} color={colors.textMuted} />
                    </Pressable>
                  ))
                ) : showingApps ? (
                  activeApps.map((app) => {
                    const cfg = APP_CONFIG[app];
                    return (
                      <Pressable key={app} style={styles.appRow} onPress={() => void openApp(app, activePhone)} accessibilityRole="button">
                        <View style={[styles.appIcon, { backgroundColor: cfg.color }]}>
                          <Ionicons name={cfg.icon} size={20} color="#fff" />
                        </View>
                        <Text style={styles.appLabel}>{cfg.label}</Text>
                        <Ionicons name="chevron-forward" size={16} color={colors.textMuted} />
                      </Pressable>
                    );
                  })
                ) : null}
              </>
            )}
          </Pressable>
        </Pressable>
      </Modal>
    );
  }

  function openSheet() {
    if (isMulti && (targets?.length ?? 0) === 1) setSelectedTarget(targets![0]);
    setStep(agreementId || connectionId ? 'root' : 'apps');
    setOpen(true);
  }

  if (inline) {
    if (onRequestExpand) {
      return (
        <>
          <View style={styles.chatIconWrap}>
            <IconActionButton
              actionKey="chat"
              icon="chatbubble-ellipses-outline"
              label={t('chatBubble.inlineButtonLabel')}
              color="#2AABEE"
              shape="round"
              onPress={openSheet}
              expandedKey={expandedKey ?? null}
              onRequestExpand={onRequestExpand}
              badge={unreadCount}
            />
          </View>

          {renderSheet()}
        </>
      );
    }

    return (
      <>
        <Pressable
          style={styles.inlineBtn}
          onPress={openSheet}
          accessibilityRole="button"
          accessibilityLabel={`${t('chatBubble.communicationAccessibilityLabel')}${label ? ` — ${label}` : ''}`}
        >
          <Animated.View style={{ transform: [{ scale: pulseAnim }] }}>
            <Ionicons name="chatbubble-ellipses-outline" size={18} color="#fff" />
          </Animated.View>
          <Text style={styles.inlineBtnText} numberOfLines={1}>{t('chatBubble.inlineButtonLabel')}</Text>
          {isMulti && (targets?.length ?? 0) > 1 && (
            <View style={styles.inlineCountBadge}>
              <Text style={styles.countBadgeText}>{targets!.length}</Text>
            </View>
          )}
        </Pressable>

        {renderSheet()}
      </>
    );
  }

  return (
    <>
      {expanded && (
        <Pressable
          style={styles.outsideCatcher}
          onPress={() => setExpanded(false)}
          accessibilityElementsHidden
          importantForAccessibility="no-hide-descendants"
        />
      )}

      {/* Μια απλή, χωρίς εικονίδιο, "γλωσσίτσα" στην πραγματική άκρη της οθόνης,
          πάνω στο (κρυμμένο) bubble — μόνο όσο το bubble είναι κρυμμένο. Ένα
          tap το φέρνει μέσα (χωρίς να ανοίγει ακόμα)· επόμενο tap πάνω στο ίδιο
          το bubble το ανοίγει. Μεγάλο πάτημα αλλάζει πλευρά. */}
      {fullyCollapsed && (
        <Pressable
          style={[styles.peekTab, side === 'left' ? styles.peekTabLeft : styles.peekTabRight, { bottom: peekTabBottom }]}
          onPress={onPressArrow}
          onLongPress={toggleSide}
          hitSlop={8}
          accessibilityRole="button"
          accessibilityLabel={t('chatBubble.communicationAccessibilityLabel')}
        />
      )}

      <View
        style={[
          styles.bubble,
          side === 'left' ? { left: spacing.lg } : { right: spacing.lg },
          { bottom },
        ]}
      >
        <View style={styles.circleWrap}>
          <Animated.View style={{ transform: [{ translateX: slideTranslateX }] }}>
            <Pressable style={styles.bubbleBtn} onPress={onPressBubble} accessibilityRole="button" accessibilityLabel={`${t('chatBubble.communicationAccessibilityLabel')}${label ? ` — ${label}` : ''}`}>
              <Ionicons name="chatbubble-ellipses-outline" size={26} color="#fff" />
              {isMulti && (targets?.length ?? 0) > 1 && (
                <View style={styles.countBadge}>
                  <Text style={styles.countBadgeText}>{targets!.length}</Text>
                </View>
              )}
            </Pressable>
          </Animated.View>
        </View>

        {!!label && !isMulti && (
          <Animated.View style={{ transform: [{ translateX: slideTranslateX }] }}>
            <View style={styles.bubbleLabel}>
              <Text style={styles.bubbleLabelText} numberOfLines={1}>{label}</Text>
            </View>
          </Animated.View>
        )}
      </View>

      {renderSheet()}
    </>
  );
}

function makeStyles(colors: AppColors) {
  return StyleSheet.create({
  inlineBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: spacing.xs,
    backgroundColor: '#2AABEE',
    borderRadius: radius.md,
    paddingVertical: spacing.sm + 2,
    paddingHorizontal: spacing.md,
  },
  inlineBtnText: {
    color: '#fff',
    fontSize: fontSize.md,
    fontWeight: '700',
  },
  chatIconWrap: {
    marginLeft: 'auto',
  },
  inlineCountBadge: {
    minWidth: 18,
    height: 18,
    borderRadius: 9,
    paddingHorizontal: 4,
    backgroundColor: 'rgba(255,255,255,0.3)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  bubble: {
    position: 'absolute',
    zIndex: 99,
    alignItems: 'center',
  },
  outsideCatcher: {
    ...StyleSheet.absoluteFillObject,
    zIndex: 90,
  },
  circleWrap: {
    width: 54,
    height: 54,
  },
  peekTab: {
    position: 'absolute',
    zIndex: 99,
    width: 28,
    height: 44,
    backgroundColor: CHAT_AQUA,
    alignItems: 'center',
    justifyContent: 'center',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.2,
    shadowRadius: 4,
    elevation: 6,
  },
  // Στρογγυλεμένη μόνο η εσωτερική πλευρά (προς το κέντρο της οθόνης) — η
  // εξωτερική εφάπτεται στην πραγματική άκρη της οθόνης.
  peekTabLeft: { left: 0, borderTopRightRadius: 14, borderBottomRightRadius: 14 },
  peekTabRight: { right: 0, borderTopLeftRadius: 14, borderBottomLeftRadius: 14 },
  bubbleLabel: {
    marginTop: spacing.sm,
    backgroundColor: CHAT_AQUA,
    borderRadius: radius.full,
    paddingHorizontal: spacing.sm,
    paddingVertical: 4,
    maxWidth: 130,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.15,
    shadowRadius: 4,
    elevation: 4,
  },
  bubbleLabelText: {
    color: colors.textOnPrimary,
    fontSize: fontSize.xs,
    fontWeight: '700',
  },
  bubbleBtn: {
    width: 54,
    height: 54,
    borderRadius: 27,
    backgroundColor: CHAT_AQUA,
    alignItems: 'center',
    justifyContent: 'center',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.22,
    shadowRadius: 8,
    elevation: 8,
  },
  countBadge: {
    position: 'absolute',
    top: -4,
    right: -4,
    minWidth: 20,
    height: 20,
    borderRadius: 10,
    paddingHorizontal: 4,
    backgroundColor: colors.textMuted,
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 2,
    borderColor: colors.background,
  },
  countBadgeText: {
    color: '#fff',
    fontSize: 11,
    fontWeight: '800',
  },
  sheetBackRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
    flex: 1,
  },
  appIconInitial: {
    color: '#fff',
    fontSize: fontSize.md,
    fontWeight: '800',
  },
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.45)',
    justifyContent: 'flex-end',
  },
  sheet: {
    backgroundColor: colors.surface,
    borderTopLeftRadius: radius.xl,
    borderTopRightRadius: radius.xl,
    padding: spacing.lg,
    gap: spacing.sm,
  },
  handle: {
    width: 40,
    height: 4,
    borderRadius: 2,
    backgroundColor: colors.border,
    alignSelf: 'center',
    marginBottom: spacing.xs,
  },
  sheetHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: spacing.xs,
  },
  sheetTitle: {
    color: colors.text,
    fontSize: fontSize.lg,
    lineHeight: fontSize.lg * 1.35,
    fontWeight: '700',
  },
  appRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.md,
    paddingVertical: spacing.sm,
    borderTopWidth: 1,
    borderTopColor: colors.border,
  },
  appIcon: {
    width: 38,
    height: 38,
    borderRadius: 19,
    alignItems: 'center',
    justifyContent: 'center',
  },
  appLabel: {
    flex: 1,
    color: colors.text,
    fontSize: fontSize.md,
    lineHeight: fontSize.md * 1.35,
    fontWeight: '600',
  },
  appSubLabel: {
    color: colors.success,
    fontSize: fontSize.xs,
    lineHeight: fontSize.xs * 1.35,
    fontWeight: '700',
    marginTop: 2,
  },
  appRowDisabled: { opacity: 0.5 },
  appLabelDisabled: { color: colors.textMuted },
  appSubLabelDisabled: {
    color: colors.textMuted,
    fontSize: fontSize.xs,
    marginTop: 2,
  },
  });
}
